import React from "react";
import {
  Box,
  Select,
  MenuItem,
  SelectChangeEvent,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ViewMode,
  QUARTERLY_PERIOD_OPTIONS,
  ANNUAL_PERIOD_OPTIONS,
  DEFAULT_QUARTERLY_LIMIT,
  DEFAULT_ANNUAL_LIMIT,
} from "./financialPeriodConfig";
import { workspaceSwitchSx } from "../../../../Dashboard/overviewPanel";
import { hapticSelection } from "../../../../../lib/haptics";

interface FinancialPeriodSelectorProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  limit: number;
  onLimitChange: (limit: number) => void;
}

const VIEW_MODE_OPTIONS: { value: ViewMode; label: string }[] = [
  { value: "quarterly", label: "Quarterly" },
  { value: "annual", label: "Annual" },
];

const FinancialPeriodSelector: React.FC<FinancialPeriodSelectorProps> = ({
  viewMode,
  onViewModeChange,
  limit,
  onLimitChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDark = theme.palette.mode === "dark";

  const periodOptions =
    viewMode === "quarterly"
      ? QUARTERLY_PERIOD_OPTIONS
      : ANNUAL_PERIOD_OPTIONS;

  const handleViewModeChange = (newViewMode: ViewMode) => {
    if (newViewMode !== viewMode) {
      hapticSelection();
      onViewModeChange(newViewMode);
      onLimitChange(
        newViewMode === "quarterly"
          ? DEFAULT_QUARTERLY_LIMIT
          : DEFAULT_ANNUAL_LIMIT
      );
    }
  };

  const handleLimitChange = (event: SelectChangeEvent<number>) => {
    hapticSelection();
    onLimitChange(event.target.value as number);
  };

  const hoverBg = isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.03)";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 1.5,
        flexWrap: "wrap",
        flexShrink: 0,
      }}
    >
      {/* Period limit selector */}
      <Select
        value={limit}
        onChange={handleLimitChange}
        size="small"
        inputProps={{ "aria-label": "Financial history range" }}
        sx={{
          minWidth: isMobile ? 120 : 140,
          height: 34,
          "@media (pointer: coarse)": { height: 50 },
          backgroundColor: "background.surface",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,

          "& .MuiSelect-select": {
            fontSize: "0.8125rem",
            fontWeight: 500,
            color: theme.palette.text.primary,
            py: 0.5,
            px: 1.5,
          },

          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },

          "&:hover": {
            backgroundColor: hoverBg,
            borderColor: isDark
              ? "rgba(255, 255, 255, 0.15)"
              : "rgba(0, 0, 0, 0.15)",
          },

          "&.Mui-focused": {
            borderColor: theme.palette.primary.main,
            backgroundColor: "background.surface",
          },

          "& .MuiSelect-icon": {
            color: theme.palette.text.secondary,
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: "background.paper",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              mt: 0.5,
              boxShadow: "none",

              "& .MuiMenuItem-root": {
                fontSize: "0.8125rem",
                fontWeight: 500,
                color: theme.palette.text.primary,
                py: 1,
                px: 1.5,
                minHeight: "auto",

                "&:hover": {
                  backgroundColor: hoverBg,
                },

                "&.Mui-selected": {
                  backgroundColor: hoverBg,
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                },
              },
            },
          },
        }}
      >
        {periodOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>

      <ToggleButtonGroup
        value={viewMode}
        exclusive
        aria-label="Financial reporting period"
        onChange={(_, value: ViewMode | null) => {
          if (value !== null) handleViewModeChange(value);
        }}
        sx={workspaceSwitchSx}
      >
        {VIEW_MODE_OPTIONS.map((option) => (
          <ToggleButton key={option.value} value={option.value}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};

export default FinancialPeriodSelector;
